// Доступный комбобокс выбора модели ИИ. У провайдера сотни моделей —
// голый <select> тут не работает: нужен поиск и подсказка прямо в строке
// варианта (картинки/бесплатность/контекст/цена), а не только id.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { useT } from '../i18n';
import type { AiModel } from './aiApi';
import { filterModels, formatContext, formatPrice, modelById, sortModels } from './models';
import styles from './ModelPicker.module.css';

export interface ModelPickerProps {
  id: string;
  label: string;
  value: string;
  onChange(id: string): void;
  models: AiModel[];
  /** Список моделей ещё в пути — поле блокируется, чтобы не выбрать модель,
      которая пропадёт после загрузки настоящего списка. */
  loading?: boolean;
  /** Оставляет в выпадающем списке только модели с поддержкой изображений. */
  visionOnly?: boolean;
  /** Пояснение над полем — сюда попадает admin.ai.source.<источник>.hint. */
  hint?: string;
}

export function ModelPicker({
  id,
  label,
  value,
  onChange,
  models,
  loading,
  visionOnly,
  hint,
}: ModelPickerProps): ReactElement {
  const t = useT();
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  // Ручной ввод id — способ задать модель, которой ещё нет в кэше провайдера.
  const [customMode, setCustomMode] = useState(false);

  const options = useMemo(
    () => sortModels(filterModels(models, { query, visionOnly })),
    [models, query, visionOnly],
  );

  // Список отфильтровался — активный пункт не должен указывать за его границы.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(options.length - 1, 0)));
  }, [options.length]);

  // Клик вне виджета закрывает список — обычное поведение комбобокса.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const selected = modelById(models, value);
  // Список уже загружен (models.length > 0), а выбранного id в нём нет —
  // либо модель сняли с продажи, либо это остаток ручного ввода из прошлого.
  const unknownSelection = value !== '' && models.length > 0 && !selected;

  function openList(): void {
    if (loading) return;
    setQuery('');
    setActiveIndex(0);
    setOpen(true);
  }

  function select(model: AiModel): void {
    onChange(model.id);
    setOpen(false);
    inputRef.current?.focus();
  }

  function selectNone(): void {
    onChange('');
    setOpen(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const model = options[activeIndex];
      if (model) select(model);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  const displayValue = !value ? '' : selected ? `${selected.id} — ${selected.name}` : value;
  const activeId = open && options[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined;

  return (
    <div className={styles.wrap} ref={rootRef}>
      <div className={styles.head}>
        <label htmlFor={id} className="field-label">
          {label}
        </label>
        <button
          type="button"
          className={`btn btn-sm ${styles.customToggle}`}
          aria-pressed={customMode}
          onClick={() => {
            setCustomMode((v) => !v);
            setOpen(false);
          }}
        >
          {t('admin.ai.model.custom')}
        </button>
      </div>

      {hint && <p className={styles.hint}>{hint}</p>}

      {customMode ? (
        <input
          id={id}
          className="input"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div className={styles.combo}>
          <input
            id={id}
            ref={inputRef}
            className="input"
            type="text"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            autoComplete="off"
            disabled={loading}
            placeholder={loading ? t('admin.ai.models.loading') : t('admin.ai.model.search')}
            value={open ? query : displayValue}
            onFocus={openList}
            onClick={openList}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
          />
          {open && (
            <ul id={listboxId} role="listbox" className={styles.listbox} aria-label={label}>
              <li
                id={`${listboxId}-opt-none`}
                role="option"
                aria-selected={value === ''}
                className={styles.option}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectNone();
                }}
              >
                <span className={styles.optionNone}>{t('admin.ai.model.none')}</span>
              </li>
              {options.map((m, i) => (
                <li
                  key={m.id}
                  id={`${listboxId}-opt-${i}`}
                  role="option"
                  aria-selected={m.id === value}
                  className={i === activeIndex ? `${styles.option} ${styles.optionActive}` : styles.option}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(m);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <span className={`${styles.optionId} mono`}>{m.id}</span>
                  <span className={styles.optionName}>{m.name}</span>
                  <span className={styles.badges}>
                    {/* Что модель умеет — видно до выбора, а не после первой
                        ошибки. Отсутствие строгого JSON помечено отдельно и
                        тревожным цветом: это единственный флаг, без которого
                        задача не заработает вообще ни в каком виде. */}
                    {!m.json && <span className={styles.badgeWarn}>{t('admin.ai.model.nojson')}</span>}
                    {m.vision && <span className={styles.badge}>{t('admin.ai.model.vision')}</span>}
                    {m.tools && <span className={styles.badge}>{t('admin.ai.model.tools')}</span>}
                    {m.reasoning && <span className={styles.badge}>{t('admin.ai.model.reasoning')}</span>}
                    {m.free && <span className={styles.badge}>{t('admin.ai.model.free')}</span>}
                    <span className={`${styles.optionMeta} mono`}>{formatContext(m, t)}</span>
                    {/* У бесплатной модели цена — это и есть слово «бесплатная»,
                        которое уже стоит бейджем слева: второй раз не повторяем. */}
                    {!m.free && (
                      <span className={`${styles.optionMeta} mono`}>{formatPrice(m, t)}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!customMode && unknownSelection && (
        <p className={styles.hint}>{t('admin.ai.model.unknown', { model: value })}</p>
      )}

      {/* Цена и контекст выбранной модели — под полем, чтобы видеть стоимость
          не раскрывая список. У бесплатной вместо цены так и написано. */}
      {selected && (
        <p className={styles.selectedMeta}>
          <span className="mono">{formatContext(selected, t)}</span>
          <span className="mono">{formatPrice(selected, t)}</span>
          {/* Те же возможности, что и в строке списка: после выбора список
              закрыт, и иначе пришлось бы открывать его заново, только чтобы
              вспомнить, умеет ли выбранная модель инструменты. */}
          {!selected.json && <span className={styles.badgeWarn}>{t('admin.ai.model.nojson')}</span>}
          {selected.vision && <span className={styles.badge}>{t('admin.ai.model.vision')}</span>}
          {selected.tools && <span className={styles.badge}>{t('admin.ai.model.tools')}</span>}
          {selected.reasoning && <span className={styles.badge}>{t('admin.ai.model.reasoning')}</span>}
        </p>
      )}
    </div>
  );
}
